/**
 * PrayerTimeService -- Server-first prayer times with Adhan.js offline fallback.
 *
 * Primary: fetch from /aweni/api/v1/prayer/times (Aladhan.com cached, DST-aware)
 * Fallback: Adhan.js local calculation (offline mode)
 *
 * Server times are cached in IndexedDB for offline use.
 */

import {
  PrayerTimes as AdhanPrayerTimes,
  CalculationMethod,
  CalculationParameters,
  Coordinates,
  HighLatitudeRule,
  Madhab,
  Qibla,
  SunnahTimes,
} from 'adhan';
import type { Lang } from '../types';
import { NativeService } from './NativeService';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type PrayerSource = 'server' | 'offline-cache' | 'local-calc';

export interface PrayerTimesData {
  fajr: Date;
  sunrise: Date;
  dhuhr: Date;
  asr: Date;
  maghrib: Date;
  isha: Date;
  nextPrayer: { name: string; time: Date; minutesUntil: number } | null;
  qiblaDirection: number;
  location: { lat: number; lng: number; city?: string };
  method: string;
  source: PrayerSource;
  hijri?: { date: string; day: string; month: string; month_ar: string; year: string };
}

export interface NextPrayer {
  name: string;
  time: Date;
  minutesUntil: number;
}

/** The 5 obligatory daily prayers (Sunrise excluded). */
export type ObligatoryPrayerKey = 'fajr' | 'dhuhr' | 'asr' | 'maghrib' | 'isha';

/** One cell in the 5-prayer Hero strip. */
export interface PrayerStripEntry {
  name: ObligatoryPrayerKey;
  time: Date;
  /** True when this prayer's adhan time has already passed today. */
  isPast: boolean;
  /** True when this is the very next upcoming prayer. */
  isNext: boolean;
}

export interface PrayerReminderConfig {
  fajr: number;
  sunrise: number;
  dhuhr: number;
  asr: number;
  maghrib: number;
  isha: number;
}

export type CalculationMethodKey =
  | 'MuslimWorldLeague'
  | 'ISNA'
  | 'Egypt'
  | 'MakkahUmmAlQura'
  | 'Karachi'
  | 'Tehran'
  | 'Singapore'
  | 'MoonsightingCommittee'
  | 'Dubai'
  | 'Qatar'
  | 'Kuwait'
  | 'Turkey'
  // Added 2026-05-05: full Aladhan method coverage (23 methods total)
  | 'JafariLeva'
  | 'Gulf'
  | 'France'
  | 'Russia'
  | 'JAKIM'
  | 'Tunisia'
  | 'Algeria'
  | 'KEMENAG'
  | 'Morocco'
  | 'Lisboa'
  | 'Jordan'
  | 'Custom';

export type MadhabKey = 'Shafi' | 'Hanafi';

/** High-latitude rule for Fajr/Isha when astronomical twilight does not
 *  reach (above ~48°). 'auto' = Adhan.js HighLatitudeRule.recommended(). */
export type HighLatitudeRuleKey = 'auto' | 'middleOfNight' | 'seventhOfNight' | 'twilightAngle';

/** Midnight reference: standard = sunset→sunrise midpoint (Sunni default),
 *  jafari = sunset→fajr midpoint (Shia). */
export type MidnightModeKey = 'standard' | 'jafari';

/** Shafaq (twilight color) — only used when method = MoonsightingCommittee. */
export type ShafaqKey = 'general' | 'ahmer' | 'abyad';

/** Per-prayer manual tune in minutes (±). Lets users align with their local
 *  mosque schedule without changing the calc method. Aladhan: `tune=` param.
 *  Adhan.js: `params.adjustments`. */
export interface PrayerTuneConfig {
  fajr: number;
  sunrise: number;
  dhuhr: number;
  asr: number;
  maghrib: number;
  isha: number;
}

/** Iqama delay (minutes after the athan when the congregation stands).
 *  Pure user preference — not astronomical. Defaults below match the
 *  most common European/Arab mosque practice. */
export interface IqamaDelaysConfig {
  fajr: number;
  dhuhr: number;
  asr: number;
  maghrib: number;
  isha: number;
}

/** Custom calculation parameters (when method = 'Custom'). */
export interface CustomMethodConfig {
  fajrAngle: number;     // 12..20°
  ishaAngle: number;     // 12..20° (use 0 if ishaInterval > 0)
  ishaInterval: number;  // minutes after Maghrib (0 = use angle)
  maghribAngle: number;  // 0..4° (Shafiʿi precaution)
}

// Map calculation method to Aladhan method ID (https://aladhan.com/calculation-methods)
// All 23 official Aladhan methods + Custom (99). Keep in sync with COUNTRY_TO_METHOD.
const METHOD_TO_ALADHAN: Record<CalculationMethodKey, number> = {
  JafariLeva: 0,
  Karachi: 1,
  ISNA: 2,
  MuslimWorldLeague: 3,
  MakkahUmmAlQura: 4,
  Egypt: 5,
  Tehran: 7,
  Gulf: 8,
  Kuwait: 9,
  Qatar: 10,
  Singapore: 11,
  France: 12,
  Turkey: 13,
  Russia: 14,
  MoonsightingCommittee: 15,
  Dubai: 16,
  JAKIM: 17,
  Tunisia: 18,
  Algeria: 19,
  KEMENAG: 20,
  Morocco: 21,
  Lisboa: 22,
  Jordan: 23,
  Custom: 99,
};

/** Display info for the settings dropdown. Order = preferred display order
 *  in the picker (regional groupings, MWL first as the safe default). */
export const METHOD_DISPLAY: Array<{ key: CalculationMethodKey; label: string; angles: string }> = [
  { key: 'MuslimWorldLeague', label: 'Muslim World League', angles: '18° / 17°' },
  { key: 'MoonsightingCommittee', label: 'Moonsighting Committee', angles: '18° / 18° + shafaq' },
  { key: 'ISNA', label: 'ISNA (North America)', angles: '15° / 15°' },
  { key: 'Egypt', label: 'Egyptian General Authority', angles: '19.5° / 17.5°' },
  { key: 'MakkahUmmAlQura', label: 'Umm al-Qura (Makkah)', angles: '18.5° / 90 min' },
  { key: 'Karachi', label: 'Karachi (Univ. Islamic Sci.)', angles: '18° / 18°' },
  { key: 'Tehran', label: 'Tehran (Geophysics)', angles: '17.7° / 14°' },
  { key: 'Gulf', label: 'Gulf Region', angles: '19.5° / 90 min' },
  { key: 'Kuwait', label: 'Kuwait', angles: '18° / 17.5°' },
  { key: 'Qatar', label: 'Qatar', angles: '18° / 90 min' },
  { key: 'Dubai', label: 'Dubai', angles: '18.2° / 18.2°' },
  { key: 'Jordan', label: 'Jordan (Awqaf)', angles: '18° / 18°' },
  { key: 'Turkey', label: 'Turkey (Diyanet)', angles: '18° / 17°' },
  { key: 'France', label: 'UOIF France', angles: '12° / 12°' },
  { key: 'Lisboa', label: 'Portugal (Lisboa)', angles: '18° / 17°' },
  { key: 'Russia', label: 'Russia', angles: '16° / 15°' },
  { key: 'Singapore', label: 'Singapore (MUIS)', angles: '20° / 18°' },
  { key: 'JAKIM', label: 'Malaysia (JAKIM)', angles: '20° / 18°' },
  { key: 'KEMENAG', label: 'Indonesia (KEMENAG)', angles: '20° / 18°' },
  { key: 'Morocco', label: 'Morocco', angles: '19° / 17°' },
  { key: 'Algeria', label: 'Algeria', angles: '18° / 17°' },
  { key: 'Tunisia', label: 'Tunisia', angles: '18° / 18°' },
  { key: 'JafariLeva', label: 'Jafari (Shia)', angles: '16° / 14°' },
  { key: 'Custom', label: 'Custom', angles: 'manual' },
];

// ISO country code → recommended calc method.
// Sources: Aladhan API authority list, Muslim Pro / IslamicFinder regional
// defaults, and per-country Islamic authority statements.
// Exposed via getRecommendedMethodForCountry() so the location flow can
// auto-pick a sensible default the first time the user grants location.
export const COUNTRY_TO_METHOD: Record<string, CalculationMethodKey> = {
  // North America
  US: 'ISNA', CA: 'ISNA', MX: 'ISNA',
  // UK / Ireland — Moonsighting handles long summer nights better than ISNA
  GB: 'MoonsightingCommittee', IE: 'MoonsightingCommittee',
  // North Africa — each country has its own official method now
  EG: 'Egypt', SD: 'Egypt', LY: 'Egypt',
  DZ: 'Algeria',
  TN: 'Tunisia',
  MA: 'Morocco', EH: 'Morocco', MR: 'Morocco',
  // Arabian Peninsula
  SA: 'MakkahUmmAlQura', YE: 'MakkahUmmAlQura',
  AE: 'Dubai',
  QA: 'Qatar',
  KW: 'Kuwait',
  BH: 'Gulf', OM: 'Gulf',
  JO: 'Jordan', PS: 'Jordan', LB: 'Jordan', SY: 'Jordan',
  IQ: 'Karachi',
  // South / Central Asia
  PK: 'Karachi', AF: 'Karachi', BD: 'Karachi',
  IN: 'Karachi', LK: 'Karachi', NP: 'Karachi',
  // Central Asia — Russia/SAMR covers Muslim communities in former USSR
  KZ: 'Russia', UZ: 'Russia', TJ: 'Russia', KG: 'Russia', TM: 'Russia',
  // Southeast Asia
  SG: 'Singapore', BN: 'Singapore',
  MY: 'JAKIM',
  ID: 'KEMENAG',
  // Iran / Turkey / Caucasus / Balkans (Diyanet is the de facto authority)
  IR: 'Tehran',
  TR: 'Turkey', AZ: 'Turkey', BA: 'Turkey', AL: 'Turkey', XK: 'Turkey', MK: 'Turkey',
  // Europe — UOIF for Francophone, Lisbon for PT, MWL elsewhere
  FR: 'France', BE: 'France', LU: 'France', MC: 'France', CH: 'France',
  PT: 'Lisboa',
  RU: 'Russia', UA: 'Russia', BY: 'Russia',
  // MWL is the standard fallback for Western Europe / Australasia / Sub-
  // Saharan Africa where there's no national Islamic authority.
  DE: 'MuslimWorldLeague', NL: 'MuslimWorldLeague', AT: 'MuslimWorldLeague',
  ES: 'MuslimWorldLeague', IT: 'MuslimWorldLeague', GR: 'MuslimWorldLeague',
  PL: 'MuslimWorldLeague', CZ: 'MuslimWorldLeague', HU: 'MuslimWorldLeague',
  // Australasia
  AU: 'MuslimWorldLeague', NZ: 'MuslimWorldLeague',
  // Sub-Saharan Africa — predominantly Muslim countries
  NG: 'MuslimWorldLeague', SN: 'MuslimWorldLeague', ML: 'MuslimWorldLeague',
  BF: 'MuslimWorldLeague', NE: 'MuslimWorldLeague', GH: 'MuslimWorldLeague',
  CI: 'MuslimWorldLeague', GM: 'MuslimWorldLeague', GW: 'MuslimWorldLeague',
  SO: 'MuslimWorldLeague', ET: 'MuslimWorldLeague', DJ: 'MuslimWorldLeague',
  KE: 'MuslimWorldLeague', TZ: 'MuslimWorldLeague', UG: 'MuslimWorldLeague',
  ZA: 'MuslimWorldLeague',
  // East Asia
  CN: 'MuslimWorldLeague', JP: 'MuslimWorldLeague', KR: 'MuslimWorldLeague',
  // Latin America (small Muslim minorities)
  BR: 'ISNA', AR: 'ISNA',
  // High latitudes — only Moonsighting Committee gives sane Fajr/Isha
  SE: 'MoonsightingCommittee', NO: 'MoonsightingCommittee', FI: 'MoonsightingCommittee',
  IS: 'MoonsightingCommittee', GL: 'MoonsightingCommittee', DK: 'MoonsightingCommittee',
};

// ISO country code → recommended Asr madhab. Hanafi for South Asia +
// Central Asia + Türkiye (the historical Hanafi region); Shafi (Standard)
// for everywhere else. Sources: classic madhab geography + Muslim Pro /
// IslamicFinder defaults.
export const COUNTRY_TO_MADHAB: Record<string, MadhabKey> = {
  // South Asia
  PK: 'Hanafi', IN: 'Hanafi', BD: 'Hanafi', AF: 'Hanafi', LK: 'Hanafi', NP: 'Hanafi',
  // Central Asia
  KZ: 'Hanafi', UZ: 'Hanafi', TJ: 'Hanafi', KG: 'Hanafi', TM: 'Hanafi',
  // Türkiye + Caucasus + Balkans (Hanafi by Ottoman heritage)
  TR: 'Hanafi', AZ: 'Hanafi', BA: 'Hanafi', AL: 'Hanafi', XK: 'Hanafi', MK: 'Hanafi',
  // China (Hui community is Hanafi)
  CN: 'Hanafi',
};

// ---------------------------------------------------------------------------
// Prayer name i18n
// ---------------------------------------------------------------------------

type PrayerKey = 'fajr' | 'sunrise' | 'dhuhr' | 'asr' | 'maghrib' | 'isha';

const PRAYER_NAMES: Record<PrayerKey, Record<Lang, string>> = {
  fajr: { en: 'Fajr', fr: 'Fajr', ar: '\u0627\u0644\u0641\u062c\u0631', es: 'Fajr', de: 'Fadschr' },
  sunrise: {
    en: 'Sunrise',
    fr: 'Lever du soleil',
    ar: '\u0627\u0644\u0634\u0631\u0648\u0642',
    es: 'Amanecer',
    de: 'Sonnenaufgang',
  },
  dhuhr: { en: 'Dhuhr', fr: 'Dhuhr', ar: '\u0627\u0644\u0638\u0647\u0631', es: 'Dhuhr', de: 'Dhuhr' },
  asr: { en: 'Asr', fr: 'Asr', ar: '\u0627\u0644\u0639\u0635\u0631', es: 'Asr', de: 'Asr' },
  maghrib: { en: 'Maghrib', fr: 'Maghrib', ar: '\u0627\u0644\u0645\u063a\u0631\u0628', es: 'Maghrib', de: 'Maghrib' },
  isha: { en: 'Isha', fr: 'Isha', ar: '\u0627\u0644\u0639\u0634\u0627\u0621', es: 'Isha', de: 'Ischa' },
};

const PRAYER_ORDER: PrayerKey[] = ['fajr', 'sunrise', 'dhuhr', 'asr', 'maghrib', 'isha'];

// ---------------------------------------------------------------------------
// Storage keys
// ---------------------------------------------------------------------------

const LS_LOCATION = 'aweni_prayer_location';
const LS_METHOD = 'aweni_prayer_method';
const LS_MADHAB = 'aweni_prayer_madhab';
const LS_REMINDERS = 'aweni_prayer_reminders';
const LS_TUNE = 'aweni_prayer_tune';
const LS_IQAMA = 'aweni_prayer_iqama';
// Tier 2 #16 — opt-in toggle to fire a 2nd notification at iqama time
// (in addition to the existing athan reminders). Default: off — most
// users only need one alert; mosque-going users explicitly enable.
const LS_IQAMA_REMIND = 'aweni_prayer_iqama_remind';
// A4 — opt-in "match my local mosque": when ON, getTodayTimes() bypasses
// the cached server (Aladhan) response and uses the offline Adhan.js
// calculation. Useful when the user's neighborhood mosque follows a
// slightly different method than what server auto-detection picked, AND
// the local calc — given the same method/madhab — matches the mosque's
// posted schedule better than the server's broader regional default.
const LS_FORCE_LOCAL_CALC = 'aweni_prayer_force_local_calc';
// Travel-aware qasr (shortening of 4-rak'ah prayers to 2). Opt-in only
// because applying qasr is a fiqh decision; AWENI surfaces the option
// when LocationService flags the user as traveling, but never auto-
// applies. The classical jurists' threshold is ~87 km from home; we
// reuse LocationService.traveling (which uses 50 km between any two
// consecutive GPS samples) to surface the recommendation, then leave
// the actual application of qasr to the user.
const LS_QASR_ENABLED = 'aweni_prayer_qasr_enabled';
// Manual Hijri ±1 day adjust. Many regions follow local moonsighting
// (e.g. Saudi Arabia announces moonsight ~12 h before Umm al-Qura's
// calculated date in some months). We let the user nudge the displayed
// Hijri date by −1, 0, or +1 day so the calendar matches their masjid's
// announcement. Default 0 (no offset).
const LS_HIJRI_OFFSET = 'aweni_hijri_offset';
const LS_HIGHLAT = 'aweni_prayer_highlat';
const LS_MIDNIGHT = 'aweni_prayer_midnight';
const LS_SHAFAQ = 'aweni_prayer_shafaq';
const LS_CUSTOM = 'aweni_prayer_custom';
// Auto-mode: when true, LocationService changes drive method + madhab from
// COUNTRY_TO_METHOD / COUNTRY_TO_MADHAB. Default true so a fresh user gets
// the correct regional method without touching settings; turning it off
// preserves the current manual values exactly. Source-of-truth lives in
// localStorage so it survives across sessions.
const LS_AUTO_METHOD = 'aweni_prayer_auto_method';
const IDB_STORE = 'aweni_prayer_cache';
const IDB_STORE_CAL = 'aweni_prayer_calendar';

// ---------------------------------------------------------------------------
// Server API config
// ---------------------------------------------------------------------------

const PRAYER_API_BASE = '/aweni/api/v1/prayer';

// ---------------------------------------------------------------------------
// IndexedDB cache helpers
// ---------------------------------------------------------------------------

function openPrayerDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    // v2: adds calendar object store. Existing v1 dbs auto-upgrade.
    const req = indexedDB.open('aweni_prayer', 2);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(IDB_STORE)) {
        db.createObjectStore(IDB_STORE, { keyPath: 'cacheKey' });
      }
      if (!db.objectStoreNames.contains(IDB_STORE_CAL)) {
        db.createObjectStore(IDB_STORE_CAL, { keyPath: 'cacheKey' });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

async function readIDBCache(key: string, store = IDB_STORE): Promise<unknown | null> {
  try {
    const db = await openPrayerDB();
    return new Promise((resolve) => {
      const tx = db.transaction(store, 'readonly');
      const objStore = tx.objectStore(store);
      const req = objStore.get(key);
      req.onsuccess = () => resolve(req.result ?? null);
      req.onerror = () => resolve(null);
    });
  } catch {
    return null;
  }
}

async function writeIDBCache(key: string, data: Record<string, unknown>, store = IDB_STORE): Promise<void> {
  try {
    const db = await openPrayerDB();
    const tx = db.transaction(store, 'readwrite');
    const objStore = tx.objectStore(store);
    objStore.put({ ...data, cacheKey: key, cachedAt: Date.now() });
  } catch {
    // Silently fail
  }
}

// ---------------------------------------------------------------------------
// Calculation method map
// ---------------------------------------------------------------------------

/** Build CalculationParameters for Adhan.js from a method key. For methods
 *  Adhan.js doesn't ship (KEMENAG, JAKIM, France 12°, Russia, etc.) we
 *  start from `Other()` and set fajrAngle/ishaAngle/ishaInterval directly
 *  so local fallback matches Aladhan's published angles within 1 minute. */
function paramsForMethod(method: CalculationMethodKey, custom?: CustomMethodConfig): CalculationParameters {
  switch (method) {
    case 'MuslimWorldLeague':       return CalculationMethod.MuslimWorldLeague();
    case 'ISNA':                    return CalculationMethod.NorthAmerica();
    case 'Egypt':                   return CalculationMethod.Egyptian();
    case 'MakkahUmmAlQura':         return CalculationMethod.UmmAlQura();
    case 'Karachi':                 return CalculationMethod.Karachi();
    case 'Tehran':                  return CalculationMethod.Tehran();
    case 'Singapore':               return CalculationMethod.Singapore();
    case 'MoonsightingCommittee':   return CalculationMethod.MoonsightingCommittee();
    case 'Dubai':                   return CalculationMethod.Dubai();
    case 'Qatar':                   return CalculationMethod.Qatar();
    case 'Kuwait':                  return CalculationMethod.Kuwait();
    case 'Turkey':                  return CalculationMethod.Turkey();
    case 'JafariLeva':              return setAngles(CalculationMethod.Other(), 16, 14);
    case 'Gulf':                    return setAngles(CalculationMethod.Other(), 19.5, 0, 90);
    case 'France':                  return setAngles(CalculationMethod.Other(), 12, 12);
    case 'Russia':                  return setAngles(CalculationMethod.Other(), 16, 15);
    case 'JAKIM':                   return setAngles(CalculationMethod.Other(), 20, 18);
    case 'Tunisia':                 return setAngles(CalculationMethod.Other(), 18, 18);
    case 'Algeria':                 return setAngles(CalculationMethod.Other(), 18, 17);
    case 'KEMENAG':                 return setAngles(CalculationMethod.Other(), 20, 18);
    case 'Morocco':                 return setAngles(CalculationMethod.Other(), 19, 17);
    case 'Lisboa':                  return setAngles(CalculationMethod.Other(), 18, 17);
    case 'Jordan':                  return setAngles(CalculationMethod.Other(), 18, 18);
    case 'Custom': {
      const c = custom ?? { fajrAngle: 18, ishaAngle: 17, ishaInterval: 0, maghribAngle: 0 };
      return setAngles(CalculationMethod.Other(), c.fajrAngle, c.ishaAngle, c.ishaInterval);
    }
    default:                        return CalculationMethod.MuslimWorldLeague();
  }
}

function setAngles(p: CalculationParameters, fajr: number, isha: number, ishaInterval = 0): CalculationParameters {
  p.fajrAngle = fajr;
  p.ishaAngle = isha;
  p.ishaInterval = ishaInterval;
  return p;
}

function getCalculationParams(
  method: CalculationMethodKey,
  madhab: MadhabKey,
  coords: Coordinates,
  opts: { tune?: PrayerTuneConfig; highLatRule?: HighLatitudeRuleKey; custom?: CustomMethodConfig } = {},
): CalculationParameters {
  const params = paramsForMethod(method, opts.custom);
  params.madhab = madhab === 'Hanafi' ? Madhab.Hanafi : Madhab.Shafi;

  // High-latitude rule: 'auto' = Adhan.js heuristic; otherwise honor the user choice.
  switch (opts.highLatRule) {
    case 'middleOfNight':   params.highLatitudeRule = HighLatitudeRule.MiddleOfTheNight; break;
    case 'seventhOfNight':  params.highLatitudeRule = HighLatitudeRule.SeventhOfTheNight; break;
    case 'twilightAngle':   params.highLatitudeRule = HighLatitudeRule.TwilightAngle; break;
    case 'auto':
    default:                params.highLatitudeRule = HighLatitudeRule.recommended(coords);
  }

  // Per-prayer manual tune (±N minutes). Maps to PrayerAdjustments at compute time.
  if (opts.tune) {
    params.adjustments = {
      fajr: opts.tune.fajr,
      sunrise: opts.tune.sunrise,
      dhuhr: opts.tune.dhuhr,
      asr: opts.tune.asr,
      maghrib: opts.tune.maghrib,
      isha: opts.tune.isha,
    };
  }

  return params;
}

// ---------------------------------------------------------------------------
// Parse "HH:MM" time string to Date for today
// ---------------------------------------------------------------------------

function parseTimeToDate(timeStr: string, dateStr?: string): Date {
  const [hours, minutes] = timeStr.split(':').map(Number);
  const d = dateStr ? new Date(dateStr + 'T00:00:00') : new Date();
  d.setHours(hours, minutes, 0, 0);
  return d;
}

// ---------------------------------------------------------------------------
// PrayerTimeService
// ---------------------------------------------------------------------------

export class PrayerTimeService {
  private location: { lat: number; lng: number; city?: string } | null = null;
  private method: CalculationMethodKey = 'MuslimWorldLeague';
  private madhab: MadhabKey = 'Shafi';
  private reminderOffsets: PrayerReminderConfig = {
    fajr: 15,
    sunrise: 0,
    dhuhr: 10,
    asr: 10,
    maghrib: 5,
    isha: 10,
  };
  // Manual per-prayer tune in minutes (±). Defaults all 0; applied to both
  // server (Aladhan tune=) and local Adhan.js (params.adjustments).
  private tune: PrayerTuneConfig = { fajr: 0, sunrise: 0, dhuhr: 0, asr: 0, maghrib: 0, isha: 0 };
  // Iqama delays in minutes after the athan. Pure user preference; not
  // astronomical. Defaults match common European/Arab mosque practice.
  private iqama: IqamaDelaysConfig = { fajr: 20, dhuhr: 10, asr: 10, maghrib: 5, isha: 10 };
  /** Tier 2 #16 — when true, BackgroundScheduler will fire a 2nd notif at
   *  iqama time. False (default) = only the existing athan/reminder notif. */
  private iqamaReminderEnabled = false;
  /** A4 — when true, getTodayTimes() ignores cachedServerTimes and uses the
   *  local Adhan.js calc. Default false (server-first, local fallback). */
  private forceLocalCalc = false;
  /** Travel-aware qasr opt-in. When ON, getQasrApplies() returns true if
   *  LocationService also reports traveling. Default false. */
  private qasrEnabled = false;
  /** Manual Hijri ±1 day offset (−1, 0, +1). 0 = no offset. */
  private hijriOffset = 0;
  private highLatRule: HighLatitudeRuleKey = 'auto';
  private midnightMode: MidnightModeKey = 'standard';
  private shafaq: ShafaqKey = 'general';
  private customMethod: CustomMethodConfig = { fajrAngle: 18, ishaAngle: 17, ishaInterval: 0, maghribAngle: 0 };
  // Auto-mode: when true, ANY change of country (via setLocation) updates
  // method + madhab from COUNTRY_TO_METHOD / COUNTRY_TO_MADHAB. Disabled
  // automatically the moment the user picks a method/madhab manually so we
  // never override an explicit preference.
  private autoMethod = true;
  // The countryCode last seen — kept so the UI can display "Using X because
  // detected MA" without needing to re-read from LocationService.
  private lastCountryCode: string | null = null;
  private cachedServerTimes: Record<string, unknown> | null = null;
  private cachedAdhanTimes: AdhanPrayerTimes | null = null;
  private cachedDate: string | null = null;
  private lastSource: PrayerSource = 'local-calc';
  private countdownInterval: ReturnType<typeof setInterval> | null = null;
  private scheduledNotifications: number[] = [];
  private locale: Lang = 'en';
  private serverFetchPromise: Promise<void> | null = null;
  private listeners: Array<() => void> = [];

  constructor() {
    this.loadFromStorage();
  }

  // ---- Lifecycle ----------------------------------------------------------

  async initialize(): Promise<void> {
    if (!this.location) {
      await this.requestGeolocation();
    }

    if (this.location) {
      // Try server first, then fall back to local
      await this.fetchServerTimes();
      if (!this.cachedServerTimes) {
        this.computeLocalTimes();
      }
      this.scheduleReminders();
    }
  }

  // ---- Public API ---------------------------------------------------------

  getTodayTimes(): PrayerTimesData | null {
    if (!this.location) return null;

    // A4 — when the user has enabled "match my local mosque", bypass the
    // server-cached response and always go through the local Adhan.js calc
    // path below. The server cache is still kept (so toggling back is free).
    if (!this.forceLocalCalc && this.cachedServerTimes) {
      return this.buildFromServerTimes(this.cachedServerTimes);
    }

    // Fallback to Adhan.js
    const times = this.getAdhanTimes();
    if (!times) return null;

    const next = this.computeNextPrayer(times);

    return {
      fajr: times.fajr,
      sunrise: times.sunrise,
      dhuhr: times.dhuhr,
      asr: times.asr,
      maghrib: times.maghrib,
      isha: times.isha,
      nextPrayer: next ?? this.computeTomorrowFajr(),
      qiblaDirection: this.getQiblaDirection(),
      location: { ...this.location },
      method: this.method,
      source: 'local-calc',
    };
  }

  getNextPrayer(): NextPrayer | null {
    const data = this.getTodayTimes();
    if (!data) return null;
    return data.nextPrayer;
  }

  /**
   * Returns the 5 obligatory daily prayers (Fajr, Dhuhr, Asr, Maghrib, Isha)
   * for today, annotated with isPast / isNext flags.  Used by the Hero 5-prayer
   * strip and by PrayerScheduleStrip.
   */
  getTodayFivePrayerStrip(now: Date = new Date()): PrayerStripEntry[] | null {
    const data = this.getTodayTimes();
    if (!data) return null;
    const keys: ObligatoryPrayerKey[] = ['fajr', 'dhuhr', 'asr', 'maghrib', 'isha'];
    const nextName = data.nextPrayer?.name ?? null;
    return keys.map(name => ({
      name,
      time: data[name] as Date,
      isPast: (data[name] as Date).getTime() <= now.getTime(),
      isNext: nextName === name,
    }));
  }

  /** Returns the most-recently-passed prayer for the rolling banner "just passed" slot. */
  getPreviousPrayer(now: Date = new Date()): NextPrayer | null {
    const data = this.getTodayTimes();
    if (!data) return null;
    const entries = (['fajr', 'sunrise', 'dhuhr', 'asr', 'maghrib', 'isha'] as PrayerKey[])
      .map(key => ({ key, time: data[key] as Date | null }))
      .filter((e): e is { key: PrayerKey; time: Date } => e.time instanceof Date);
    const past = entries.filter(e => e.time.getTime() <= now.getTime());
    if (!past.length) return null;
    const prev = past[past.length - 1];
    return { name: prev.key, time: prev.time, minutesUntil: 0 };
  }

  /** Returns next `n` prayers spanning today + tomorrow; each entry carries `isTomorrow`. */
  getUpcomingBannerEntries(n: number, now: Date = new Date()): Array<NextPrayer & { isTomorrow: boolean }> {
    const todayData = this.getTodayTimes();
    if (!todayData) return [];
    const tomorrow = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1);
    const tomorrowData = this.getTimesForDate(tomorrow);
    const toEntries = (data: PrayerTimesData, isTomorrow: boolean) =>
      (['fajr', 'sunrise', 'dhuhr', 'asr', 'maghrib', 'isha'] as PrayerKey[])
        .map(key => ({ key, time: data[key] as Date | null, isTomorrow }))
        .filter((e): e is { key: PrayerKey; time: Date; isTomorrow: boolean } =>
          e.time instanceof Date);
    const all = [
      ...toEntries(todayData, false),
      ...(tomorrowData ? toEntries(tomorrowData, true) : []),
    ];
    return all
      .filter(e => e.time.getTime() > now.getTime())
      .slice(0, n)
      .map(e => ({
        name: e.key,
        time: e.time,
        minutesUntil: Math.round((e.time.getTime() - now.getTime()) / 60_000),
        isTomorrow: e.isTomorrow,
      }));
  }

  getSource(): PrayerSource {
    return this.lastSource;
  }

  getQiblaDirection(): number {
    if (!this.location) return 0;
    const coords = new Coordinates(this.location.lat, this.location.lng);
    return Qibla(coords);
  }

  setMethod(method: CalculationMethodKey): void {
    this.method = method;
    localStorage.setItem(LS_METHOD, method);
    // Manual selection turns auto OFF — the user has expressed an explicit
    // preference and we must not overwrite it on the next location change.
    this.autoMethod = false;
    localStorage.setItem(LS_AUTO_METHOD, 'false');
    this.invalidateCache();
    // Re-fetch from server with new method
    this.fetchServerTimes().then(() => this.scheduleReminders()).then(() => this.notifyChange());
  }

  setMadhab(madhab: MadhabKey): void {
    this.madhab = madhab;
    localStorage.setItem(LS_MADHAB, madhab);
    // Same rationale as setMethod — manual madhab pick disables auto.
    this.autoMethod = false;
    localStorage.setItem(LS_AUTO_METHOD, 'false');
    this.invalidateCache();
    this.fetchServerTimes().then(() => this.scheduleReminders()).then(() => this.notifyChange());
  }

  getMadhab(): MadhabKey {
    return this.madhab;
  }

  /** True when method/madhab follow the user's country automatically. */
  isAutoMethod(): boolean { return this.autoMethod; }

  /** Set auto-mode. When turned ON, immediately re-pick method+madhab from
   *  the last-seen countryCode so the user sees the change reflected. */
  setAutoMethod(enabled: boolean): void {
    this.autoMethod = enabled;
    localStorage.setItem(LS_AUTO_METHOD, String(enabled));
    if (enabled && this.lastCountryCode) {
      this.applyCountryDefaults(this.lastCountryCode);
      this.invalidateCache();
      this.fetchServerTimes().then(() => this.scheduleReminders()).then(() => this.notifyChange());
    } else {
      this.notifyChange();
    }
  }

  /** The last ISO country code passed to setLocation — drives the
   *  "Using Morocco method (because country=MA)" hint in settings. */
  getLastCountryCode(): string | null { return this.lastCountryCode; }

  /** Apply COUNTRY_TO_METHOD + COUNTRY_TO_MADHAB without persisting them
   *  as user-chosen values (so future country changes still apply). */
  private applyCountryDefaults(countryCode: string): void {
    const cc = countryCode.toUpperCase();
    const recommendedMethod = COUNTRY_TO_METHOD[cc];
    if (recommendedMethod) this.method = recommendedMethod;
    const recommendedMadhab = COUNTRY_TO_MADHAB[cc];
    // Default Shafi for any country not explicitly Hanafi — prevents the
    // user being stuck on Hanafi when moving from Pakistan to a Shafi region.
    this.madhab = recommendedMadhab ?? 'Shafi';
  }

  setLocation(lat: number, lng: number, city?: string, countryCode?: string): void {
    this.location = { lat, lng, city };
    localStorage.setItem(LS_LOCATION, JSON.stringify(this.location));
    this.invalidateCache();

    if (countryCode) this.lastCountryCode = countryCode.toUpperCase();

    // Auto-mode: ANY country change re-applies the regional method+madhab.
    // Without auto-mode: method+madhab remain at whatever the user set.
    if (this.autoMethod && countryCode) {
      this.applyCountryDefaults(countryCode);
    }

    this.fetchServerTimes().then(() => this.scheduleReminders()).then(() => this.notifyChange());
  }

  /** Returns the recommended calc method for an ISO 3166-1 alpha-2 country
   *  code, or undefined if none is mapped. Use this from the location flow
   *  to suggest a default; user choice always takes precedence. */
  static getRecommendedMethodForCountry(code: string | null | undefined): CalculationMethodKey | undefined {
    if (!code) return undefined;
    return COUNTRY_TO_METHOD[code.toUpperCase()];
  }

  /** Returns the recommended Asr madhab for a country code (Hanafi for
   *  South/Central Asia + Türkiye, Shafi/Standard everywhere else). */
  static getRecommendedMadhabForCountry(code: string | null | undefined): MadhabKey {
    if (!code) return 'Shafi';
    return COUNTRY_TO_MADHAB[code.toUpperCase()] ?? 'Shafi';
  }

  setReminderOffset(prayer: string, minutes: number): void {
    const key = prayer.toLowerCase() as PrayerKey;
    if (key in this.reminderOffsets) {
      this.reminderOffsets[key] = Math.max(0, Math.min(60, minutes));
      localStorage.setItem(LS_REMINDERS, JSON.stringify(this.reminderOffsets));
      this.scheduleReminders();
    }
  }

  getReminderOffsets(): PrayerReminderConfig {
    return { ...this.reminderOffsets };
  }

  // ---- Tune (per-prayer ± minutes) -------------------------------------

  getTune(): PrayerTuneConfig {
    return { ...this.tune };
  }

  setTune(patch: Partial<PrayerTuneConfig>): void {
    Object.assign(this.tune, patch);
    localStorage.setItem(LS_TUNE, JSON.stringify(this.tune));
    this.invalidateCache();
    this.fetchServerTimes().then(() => this.scheduleReminders()).then(() => this.notifyChange());
  }

  // ---- Iqama delays (minutes after athan) ------------------------------

  getIqamaDelays(): IqamaDelaysConfig {
    return { ...this.iqama };
  }

  setIqamaDelays(patch: Partial<IqamaDelaysConfig>): void {
    Object.assign(this.iqama, patch);
    localStorage.setItem(LS_IQAMA, JSON.stringify(this.iqama));
    this.notifyChange();
  }

  /** Tier 2 #16 — opt-in 2nd notification at iqama time. */
  getIqamaReminderEnabled(): boolean {
    return this.iqamaReminderEnabled;
  }

  setIqamaReminderEnabled(enabled: boolean): void {
    this.iqamaReminderEnabled = !!enabled;
    try { localStorage.setItem(LS_IQAMA_REMIND, this.iqamaReminderEnabled ? '1' : '0'); } catch { /* */ }
    this.notifyChange();
  }

  /** A4 — opt-in: prefer local Adhan.js calc over the server (Aladhan). */
  getForceLocalCalc(): boolean {
    return this.forceLocalCalc;
  }

  setForceLocalCalc(enabled: boolean): void {
    this.forceLocalCalc = !!enabled;
    try { localStorage.setItem(LS_FORCE_LOCAL_CALC, this.forceLocalCalc ? '1' : '0'); } catch { /* */ }
    this.notifyChange();
  }

  /** Travel-aware qasr: opt-in toggle. */
  getQasrEnabled(): boolean {
    return this.qasrEnabled;
  }

  setQasrEnabled(enabled: boolean): void {
    this.qasrEnabled = !!enabled;
    try { localStorage.setItem(LS_QASR_ENABLED, this.qasrEnabled ? '1' : '0'); } catch { /* */ }
    this.notifyChange();
  }

  /**
   * Returns true when qasr applies right now: user opted in AND
   * LocationService reports traveling. UI surfaces a banner to remind
   * the user to shorten Dhuhr/Asr/Isha to 2 rak'ah. AWENI never
   * auto-shortens — the rakaat count is informational.
   */
  getQasrApplies(): boolean {
    if (!this.qasrEnabled) return false;
    try {
      const raw = localStorage.getItem('aweni_location');
      if (!raw) return false;
      const s = JSON.parse(raw) as { traveling?: boolean };
      return !!s.traveling;
    } catch { return false; }
  }

  /** Manual Hijri ±1 day offset (−1, 0, or +1). */
  getHijriOffset(): number {
    return this.hijriOffset;
  }

  setHijriOffset(days: number): void {
    const clamped = Math.max(-2, Math.min(2,Math.round(days)));
    this.hijriOffset = clamped;
    try { localStorage.setItem(LS_HIJRI_OFFSET, String(clamped)); } catch { /* */ }
    this.notifyChange();
  }

  /** Returns Date objects for each iqama (athan time + iqama delay). */
  getIqamaTimes(): Record<'fajr' | 'dhuhr' | 'asr' | 'maghrib' | 'isha', Date> | null {
    const data = this.getTodayTimes();
    if (!data) return null;
    return {
      fajr: new Date(data.fajr.getTime() + this.iqama.fajr * 60_000),
      dhuhr: new Date(data.dhuhr.getTime() + this.iqama.dhuhr * 60_000),
      asr: new Date(data.asr.getTime() + this.iqama.asr * 60_000),
      maghrib: new Date(data.maghrib.getTime() + this.iqama.maghrib * 60_000),
      isha: new Date(data.isha.getTime() + this.iqama.isha * 60_000),
    };
  }

  // ---- High-latitude rule, midnight mode, shafaq, custom ----------------

  getHighLatRule(): HighLatitudeRuleKey { return this.highLatRule; }
  setHighLatRule(rule: HighLatitudeRuleKey): void {
    this.highLatRule = rule;
    localStorage.setItem(LS_HIGHLAT, rule);
    this.invalidateCache();
    this.fetchServerTimes().then(() => this.scheduleReminders()).then(() => this.notifyChange());
  }

  getMidnightMode(): MidnightModeKey { return this.midnightMode; }
  setMidnightMode(mode: MidnightModeKey): void {
    this.midnightMode = mode;
    localStorage.setItem(LS_MIDNIGHT, mode);
    this.invalidateCache();
    this.fetchServerTimes().then(() => this.notifyChange());
  }

  getShafaq(): ShafaqKey { return this.shafaq; }
  setShafaq(sh: ShafaqKey): void {
    this.shafaq = sh;
    localStorage.setItem(LS_SHAFAQ, sh);
    this.invalidateCache();
    this.fetchServerTimes().then(() => this.notifyChange());
  }

  getCustomMethod(): CustomMethodConfig { return { ...this.customMethod }; }
  setCustomMethod(patch: Partial<CustomMethodConfig>): void {
    Object.assign(this.customMethod, patch);
    localStorage.setItem(LS_CUSTOM, JSON.stringify(this.customMethod));
    if (this.method === 'Custom') {
      this.invalidateCache();
      this.fetchServerTimes().then(() => this.scheduleReminders()).then(() => this.notifyChange());
    } else {
      this.notifyChange();
    }
  }

  // ---- Change notification (UI refresh) --------------------------------

  onChange(fn: () => void): () => void {
    this.listeners.push(fn);
    return () => { this.listeners = this.listeners.filter((l) => l !== fn); };
  }

  private notifyChange(): void {
    for (const l of this.listeners) {
      try { l(); } catch { /* listener errors must not break service */ }
    }
  }

  private invalidateCache(): void {
    this.cachedServerTimes = null;
    this.cachedAdhanTimes = null;
    this.cachedDate = null;
  }

  scheduleReminders(): void {
    this.clearScheduledNotifications();

    const data = this.getTodayTimes();
    if (!data) return;

    const now = Date.now();
    const prayerEntries: Array<{ key: PrayerKey; time: Date }> = [
      { key: 'fajr', time: data.fajr },
      { key: 'sunrise', time: data.sunrise },
      { key: 'dhuhr', time: data.dhuhr },
      { key: 'asr', time: data.asr },
      { key: 'maghrib', time: data.maghrib },
      { key: 'isha', time: data.isha },
    ];

    // On native platforms, use Capacitor LocalNotifications (persistent, survives background)
    if (NativeService.isNative()) {
      for (const { key, time } of prayerEntries) {
        const offsetMinutes = this.reminderOffsets[key];
        const name = this.getPrayerName(key);
        const timeStr = this.formatTime(time);
        // Stable IDs: prayer index * 10 + type (0=reminder, 1=athan)
        const prayerIdx = PRAYER_ORDER.indexOf(key);

        if (offsetMinutes > 0) {
          const reminderAt = new Date(time.getTime() - offsetMinutes * 60_000);
          if (reminderAt.getTime() > now) {
            NativeService.scheduleLocalNotification({
              title: `${name} in ${offsetMinutes} minutes`,
              body: `${name} at ${timeStr}`,
              at: reminderAt,
              id: 5000 + prayerIdx * 10,
            });
          }
        }

        // Athan notification at exact prayer time
        if (time.getTime() > now) {
          NativeService.scheduleLocalNotification({
            title: name,
            body: `It is time for ${name} prayer`,
            at: time,
            id: 5001 + prayerIdx * 10,
          });
        }
      }
      return;
    }

    // Web fallback: setTimeout + Notification API
    if (!('Notification' in window) || Notification.permission !== 'granted') {
      return;
    }

    for (const { key, time } of prayerEntries) {
      const offsetMinutes = this.reminderOffsets[key];
      if (offsetMinutes <= 0) continue;

      const reminderTime = time.getTime() - offsetMinutes * 60_000;
      const delay = reminderTime - now;

      if (delay > 0 && delay < 24 * 60 * 60_000) {
        const timeoutId = window.setTimeout(() => {
          this.showPrayerNotification(key, time);
        }, delay);
        this.scheduledNotifications.push(timeoutId);
      }

      const athanDelay = time.getTime() - now;
      if (athanDelay > 0 && athanDelay < 24 * 60 * 60_000) {
        const athanId = window.setTimeout(() => {
          this.showAthanNotification(key);
        }, athanDelay);
        this.scheduledNotifications.push(athanId);
      }
    }
  }

  startCountdown(callback: (data: { minutesUntil: number; secondsUntil: number }) => void): () => void {
    const tick = () => {
      const np = this.getNextPrayer();
      if (np) {
        const data = this.getTodayTimes();
        let prayerTime = np.time;
        if (data) {
          const key = np.name as keyof Pick<PrayerTimesData, 'fajr' | 'sunrise' | 'dhuhr' | 'asr' | 'maghrib' | 'isha'>;
          if (key in data) prayerTime = data[key];
        }
        const diffMs = prayerTime.getTime() - Date.now();
        const secondsUntil = Math.max(0, Math.floor(diffMs / 1000));
        callback({ minutesUntil: np.minutesUntil, secondsUntil });
      } else {
        callback({ minutesUntil: -1, secondsUntil: -1 });
      }
    };

    tick();

    const interval = setInterval(tick, 1000); // Update every second for live countdown
    this.countdownInterval = interval;

    return () => {
      clearInterval(interval);
      if (this.countdownInterval === interval) {
        this.countdownInterval = null;
      }
    };
  }

  setLocale(locale: Lang): void {
    this.locale = locale;
  }

  getPrayerName(key: PrayerKey, lang?: Lang): string {
    const l = lang ?? this.locale;
    return PRAYER_NAMES[key]?.[l] ?? PRAYER_NAMES[key]?.en ?? key;
  }

  getAllPrayerNames(lang?: Lang): Record<PrayerKey, string> {
    const l = lang ?? this.locale;
    const result = {} as Record<PrayerKey, string>;
    for (const key of PRAYER_ORDER) {
      result[key] = PRAYER_NAMES[key][l];
    }
    return result;
  }

  formatTime(date: Date, lang?: Lang, tz?: string): string {
    const l = lang ?? this.locale;
    const use24h = l === 'fr' || l === 'de';
    const opts: Intl.DateTimeFormatOptions = {
      hour: '2-digit',
      minute: '2-digit',
      hour12: !use24h,
    };
    // When a tz is provided (e.g., user is viewing prayer times for Sydney
    // from a Casablanca device), format the time IN THAT timezone — not the
    // device tz. Without this, Adhan.js's UTC moments get rendered in the
    // device's local clock = wrong by the tz offset (Sydney off by 9–11h).
    if (tz) opts.timeZone = tz;
    try {
      return new Intl.DateTimeFormat(l === 'ar' ? 'ar-SA' : l, opts).format(date);
    } catch {
      return date.toLocaleTimeString(undefined, {
        hour: '2-digit',
        minute: '2-digit',
      });
    }
  }

  /** Same time, both formats — used for dense displays where the user
   *  reads either 24h or 12h. Returns `{ h24: "13:27", h12: "1:27 PM" }`.
   *  When tz is provided, both formats reflect that timezone. */
  formatTimePair(date: Date, lang?: Lang, tz?: string): { h24: string; h12: string } {
    const l = lang ?? this.locale;
    const opts24: Intl.DateTimeFormatOptions = { hour: '2-digit', minute: '2-digit', hour12: false };
    const opts12: Intl.DateTimeFormatOptions = { hour: '2-digit', minute: '2-digit', hour12: true };
    if (tz) { opts24.timeZone = tz; opts12.timeZone = tz; }
    try {
      const locArg = l === 'ar' ? 'ar-SA' : l;
      return {
        h24: new Intl.DateTimeFormat(locArg, opts24).format(date),
        h12: new Intl.DateTimeFormat(locArg === 'ar-SA' ? 'en-US' : locArg, opts12).format(date),
      };
    } catch {
      return { h24: date.toLocaleTimeString(undefined, opts24), h12: date.toLocaleTimeString(undefined, opts12) };
    }
  }

  /** Compute prayer times for arbitrary coords using the user's current
   *  settings (method, madhab, tune, etc.). Does NOT mutate service state.
   *  Source is always 'local-calc' — for the live device location, prefer
   *  getTodayTimes() which uses server-cached Aladhan times. */
  getTimesForCoords(lat: number, lng: number, date: Date = new Date()): PrayerTimesData {
    const coords = new Coordinates(lat, lng);
    const params = getCalculationParams(this.method, this.madhab, coords, {
      tune: this.tune,
      highLatRule: this.highLatRule,
      custom: this.customMethod,
    });
    const times = new AdhanPrayerTimes(coords, date, params);

    const now = new Date();
    const entries: Array<{ key: PrayerKey; time: Date }> = [
      { key: 'fajr', time: times.fajr },
      { key: 'sunrise', time: times.sunrise },
      { key: 'dhuhr', time: times.dhuhr },
      { key: 'asr', time: times.asr },
      { key: 'maghrib', time: times.maghrib },
      { key: 'isha', time: times.isha },
    ];
    let next: NextPrayer | null = null;
    for (const e of entries) {
      if (e.time > now) {
        next = { name: e.key, time: e.time, minutesUntil: Math.round((e.time.getTime() - now.getTime()) / 60_000) };
        break;
      }
    }
    if (!next) {
      // Tomorrow's Fajr at this location
      const tomorrow = new Date(date);
      tomorrow.setDate(tomorrow.getDate() + 1);
      const tParams = getCalculationParams(this.method, this.madhab, coords, {
        tune: this.tune, highLatRule: this.highLatRule, custom: this.customMethod,
      });
      const tTimes = new AdhanPrayerTimes(coords, tomorrow, tParams);
      next = { name: 'fajr', time: tTimes.fajr, minutesUntil: Math.round((tTimes.fajr.getTime() - now.getTime()) / 60_000) };
    }

    return {
      fajr: times.fajr,
      sunrise: times.sunrise,
      dhuhr: times.dhuhr,
      asr: times.asr,
      maghrib: times.maghrib,
      isha: times.isha,
      nextPrayer: next,
      qiblaDirection: Qibla(coords),
      location: { lat, lng },
      method: this.method,
      source: 'local-calc',
    };
  }

  getTimesForDate(date: Date): PrayerTimesData | null {
    if (!this.location) return null;

    const coords = new Coordinates(this.location.lat, this.location.lng);
    const params = getCalculationParams(this.method, this.madhab, coords, {
      tune: this.tune,
      highLatRule: this.highLatRule,
      custom: this.customMethod,
    });
    const times = new AdhanPrayerTimes(coords, date, params);

    const next = this.computeNextPrayer(times);

    return {
      fajr: times.fajr,
      sunrise: times.sunrise,
      dhuhr: times.dhuhr,
      asr: times.asr,
      maghrib: times.maghrib,
      isha: times.isha,
      nextPrayer: next ?? this.computeTomorrowFajr(),
      qiblaDirection: this.getQiblaDirection(),
      location: { ...this.location },
      method: this.method,
      source: 'local-calc',
    };
  }

  /** Returns tomorrow's Fajr as the next prayer when today's are all done. */
  private computeTomorrowFajr(): NextPrayer | null {
    if (!this.location) return null;
    const coords = new Coordinates(this.location.lat, this.location.lng);
    const params = getCalculationParams(this.method, this.madhab, coords, {
      tune: this.tune,
      highLatRule: this.highLatRule,
      custom: this.customMethod,
    });
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    const t = new AdhanPrayerTimes(coords, tomorrow, params);
    const minutesUntil = Math.round((t.fajr.getTime() - Date.now()) / 60_000);
    return { name: 'fajr', time: t.fajr, minutesUntil };
  }

  getSunnahTimes(): { middleOfTheNight: Date; lastThirdOfTheNight: Date } | null {
    const times = this.getAdhanTimes();
    if (!times) return null;

    const sunnah = new SunnahTimes(times);
    return {
      middleOfTheNight: sunnah.middleOfTheNight,
      lastThirdOfTheNight: sunnah.lastThirdOfTheNight,
    };
  }

  getLocation(): { lat: number; lng: number; city?: string } | null {
    return this.location ? { ...this.location } : null;
  }

  getMethod(): CalculationMethodKey {
    return this.method;
  }

  isReady(): boolean {
    return this.location !== null;
  }

  destroy(): void {
    this.clearScheduledNotifications();
    if (this.countdownInterval) {
      clearInterval(this.countdownInterval);
      this.countdownInterval = null;
    }
  }

  // ---- Server fetch -------------------------------------------------------

  private async fetchServerTimes(): Promise<void> {
    if (!this.location) return;
    if (this.serverFetchPromise) return this.serverFetchPromise;

    this.serverFetchPromise = this._doFetchServerTimes();
    try {
      await this.serverFetchPromise;
    } finally {
      this.serverFetchPromise = null;
    }
  }

  private buildServerQuery(date: string): string {
    if (!this.location) return '';
    const methodId = METHOD_TO_ALADHAN[this.method] ?? 3;
    const params = new URLSearchParams({
      lat: String(this.location.lat),
      lng: String(this.location.lng),
      date,
      method: String(methodId),
    });

    // school: 0 = standard (Shafiʿi/Maliki/Hanbali), 1 = Hanafi
    params.set('school', this.madhab === 'Hanafi' ? '1' : '0');

    // Per-prayer tune as Aladhan tune= comma string:
    // imsak,fajr,sunrise,dhuhr,asr,maghrib,sunset,isha,midnight (9 ints)
    const t = this.tune;
    const tuneStr = `0,${t.fajr},${t.sunrise},${t.dhuhr},${t.asr},${t.maghrib},0,${t.isha},0`;
    if (tuneStr !== '0,0,0,0,0,0,0,0,0') params.set('tune', tuneStr);

    // High-latitude rule
    const hlMap: Record<HighLatitudeRuleKey, string | null> = {
      auto: null,
      middleOfNight: '1',
      seventhOfNight: '2',
      twilightAngle: '3',
    };
    const hlVal = hlMap[this.highLatRule];
    if (hlVal) params.set('latitudeAdjustmentMethod', hlVal);

    // Midnight mode
    if (this.midnightMode === 'jafari') params.set('midnightMode', '1');

    // Shafaq (only meaningful for method 15 MoonsightingCommittee)
    if (this.method === 'MoonsightingCommittee' && this.shafaq !== 'general') {
      params.set('shafaq', this.shafaq);
    }

    // Custom method angles (Aladhan methodSettings=fajr,maghrib,isha)
    if (this.method === 'Custom') {
      const c = this.customMethod;
      const ishaVal = c.ishaInterval > 0 ? `${c.ishaInterval}` : `${c.ishaAngle}`;
      params.set('methodSettings', `${c.fajrAngle},${c.maghribAngle},${ishaVal}`);
    }

    return params.toString();
  }

  private async _doFetchServerTimes(): Promise<void> {
    if (!this.location) return;

    const date = new Date().toISOString().split('T')[0];
    const methodId = METHOD_TO_ALADHAN[this.method] ?? 3;
    const tuneFingerprint = `${this.tune.fajr}.${this.tune.dhuhr}.${this.tune.asr}.${this.tune.maghrib}.${this.tune.isha}`;
    const cacheKey = `${this.location.lat.toFixed(2)}_${this.location.lng.toFixed(2)}_${date}_m${methodId}_${this.madhab}_${this.highLatRule}_${this.midnightMode}_t${tuneFingerprint}`;

    try {
      const url = `${PRAYER_API_BASE}/times?${this.buildServerQuery(date)}`;
      const resp = await fetch(url, { cache: 'no-store' });

      if (resp.ok) {
        const json = await resp.json();
        if (json.ok && json.data?.timings) {
          this.cachedServerTimes = json.data;
          this.lastSource = 'server';
          // Save to IndexedDB for offline
          await writeIDBCache(cacheKey, json.data);
          return;
        }
      }
    } catch {
      // Network error -- try IndexedDB cache
    }

    // Try IndexedDB offline cache
    const cached = await readIDBCache(cacheKey) as Record<string, unknown> | null;
    if (cached && cached.timings) {
      this.cachedServerTimes = cached;
      this.lastSource = 'offline-cache';
      return;
    }

    // Fall back to Adhan.js local calculation
    this.cachedServerTimes = null;
    this.lastSource = 'local-calc';
    this.computeLocalTimes();
  }

  // ---- Calendar (30-day) — for the Prayer module page ------------------

  /** Fetch the full month for the active location + method via the
   *  /v1/prayer/calendar endpoint (single round-trip; backend caches the
   *  Aladhan response for 12 h). Result is mirrored to IndexedDB for
   *  offline use. */
  async fetchCalendar(year: number, month: number): Promise<Array<Record<string, unknown>> | null> {
    if (!this.location) return null;
    const tuneFingerprint = `${this.tune.fajr}.${this.tune.dhuhr}.${this.tune.asr}.${this.tune.maghrib}.${this.tune.isha}`;
    const methodId = METHOD_TO_ALADHAN[this.method] ?? 3;
    const cacheKey = `${this.location.lat.toFixed(2)}_${this.location.lng.toFixed(2)}_${year}_${month}_m${methodId}_${this.madhab}_${this.highLatRule}_${this.midnightMode}_t${tuneFingerprint}`;

    // 1) Fresh IDB cache (24h TTL — same as backend)
    const cached = await readIDBCache(cacheKey, IDB_STORE_CAL) as { days?: unknown[]; cachedAt?: number } | null;
    if (cached?.days && cached.cachedAt && Date.now() - cached.cachedAt < 86_400_000) {
      return cached.days as Array<Record<string, unknown>>;
    }

    // 2) Single backend call — backend forwards to Aladhan /calendar/Y/M
    try {
      const baseQ = this.buildServerQuery(new Date().toISOString().split('T')[0]);
      // buildServerQuery embeds date=...; replace with year/month
      const params = new URLSearchParams(baseQ);
      params.delete('date');
      params.set('year', String(year));
      params.set('month', String(month));
      const url = `${PRAYER_API_BASE}/calendar?${params.toString()}`;
      const resp = await fetch(url, { cache: 'no-store' });
      if (resp.ok) {
        const json = await resp.json();
        const days = (json?.data?.days ?? json?.days) as Array<Record<string, unknown>> | undefined;
        if (Array.isArray(days) && days.length > 0) {
          await writeIDBCache(cacheKey, { days }, IDB_STORE_CAL);
          return days;
        }
      }
    } catch {
      // Network error — fall through to stale cache or null
    }

    if (cached?.days) return cached.days as Array<Record<string, unknown>>;
    return null;
  }

  /** Synchronous read of the calendar IDB cache. Returns null if absent.
   *  Use to render instantly while `fetchCalendar` revalidates in bg. */
  async peekCalendar(year: number, month: number): Promise<Array<Record<string, unknown>> | null> {
    if (!this.location) return null;
    const methodId = METHOD_TO_ALADHAN[this.method] ?? 3;
    const cacheKey = `${this.location.lat.toFixed(2)}_${this.location.lng.toFixed(2)}_${year}_${month}_m${methodId}_${this.madhab}`;
    const cached = await readIDBCache(cacheKey, IDB_STORE_CAL) as { days?: unknown[] } | null;
    return (cached?.days as Array<Record<string, unknown>>) ?? null;
  }

  private buildFromServerTimes(serverData: Record<string, unknown>): PrayerTimesData | null {
    if (!this.location) return null;

    const timings = serverData.timings as Record<string, string>;
    if (!timings) return null;

    const dateStr = serverData.date as string | undefined;

    const fajr = parseTimeToDate(timings.Fajr, dateStr);
    const sunrise = parseTimeToDate(timings.Sunrise, dateStr);
    const dhuhr = parseTimeToDate(timings.Dhuhr, dateStr);
    const asr = parseTimeToDate(timings.Asr, dateStr);
    const maghrib = parseTimeToDate(timings.Maghrib, dateStr);
    const isha = parseTimeToDate(timings.Isha, dateStr);

    const now = new Date();
    const entries: Array<{ key: PrayerKey; time: Date }> = [
      { key: 'fajr', time: fajr },
      { key: 'sunrise', time: sunrise },
      { key: 'dhuhr', time: dhuhr },
      { key: 'asr', time: asr },
      { key: 'maghrib', time: maghrib },
      { key: 'isha', time: isha },
    ];

    let nextPrayer: NextPrayer | null = null;
    for (const entry of entries) {
      if (entry.time > now) {
        const minutesUntil = Math.round((entry.time.getTime() - now.getTime()) / 60_000);
        nextPrayer = { name: entry.key, time: entry.time, minutesUntil };
        break;
      }
    }

    return {
      fajr,
      sunrise,
      dhuhr,
      asr,
      maghrib,
      isha,
      nextPrayer: nextPrayer ?? this.computeTomorrowFajr(),
      qiblaDirection: this.getQiblaDirection(),
      location: { ...this.location },
      method: this.method,
      source: this.lastSource,
      hijri: serverData.hijri as PrayerTimesData['hijri'],
    };
  }

  // ---- Private helpers ----------------------------------------------------

  private loadFromStorage(): void {
    try {
      const locStr = localStorage.getItem(LS_LOCATION);
      if (locStr) {
        this.location = JSON.parse(locStr);
      }
    } catch {
      // corrupt data
    }

    const method = localStorage.getItem(LS_METHOD) as CalculationMethodKey | null;
    if (method && method in METHOD_TO_ALADHAN) {
      this.method = method;
    }

    const madhab = localStorage.getItem(LS_MADHAB) as MadhabKey | null;
    if (madhab === 'Shafi' || madhab === 'Hanafi') {
      this.madhab = madhab;
    }

    // Auto-mode default = TRUE for fresh users; once the user explicitly
    // picks a method/madhab, this gets persisted as 'false' so the choice
    // sticks across sessions. The presence of `LS_METHOD` in storage is
    // ALSO a signal that the user has touched method settings — for
    // backward compatibility we treat that as "auto off" too.
    const autoStr = localStorage.getItem(LS_AUTO_METHOD);
    if (autoStr === 'true') this.autoMethod = true;
    else if (autoStr === 'false') this.autoMethod = false;
    else this.autoMethod = !localStorage.getItem(LS_METHOD); // legacy default

    try {
      const remStr = localStorage.getItem(LS_REMINDERS);
      if (remStr) {
        const parsed = JSON.parse(remStr) as Partial<PrayerReminderConfig>;
        Object.assign(this.reminderOffsets, parsed);
      }
    } catch {
      // corrupt data
    }

    try {
      const tuneStr = localStorage.getItem(LS_TUNE);
      if (tuneStr) {
        const parsed = JSON.parse(tuneStr) as Partial<PrayerTuneConfig>;
        Object.assign(this.tune, parsed);
      }
    } catch { /* corrupt */ }

    try {
      const iqamaStr = localStorage.getItem(LS_IQAMA);
      if (iqamaStr) {
        const parsed = JSON.parse(iqamaStr) as Partial<IqamaDelaysConfig>;
        Object.assign(this.iqama, parsed);
      }
      // Tier 2 #16 — iqama reminder toggle (default off; '1' = on)
      this.iqamaReminderEnabled = localStorage.getItem(LS_IQAMA_REMIND) === '1';
      // A4 — match-my-local-mosque toggle (default off; '1' = on)
      this.forceLocalCalc = localStorage.getItem(LS_FORCE_LOCAL_CALC) === '1';
      // Travel-aware qasr opt-in (default off; '1' = on)
      this.qasrEnabled = localStorage.getItem(LS_QASR_ENABLED) === '1';
      // Manual Hijri offset (clamped to −1..+1 on read)
      const ho = parseInt(localStorage.getItem(LS_HIJRI_OFFSET) || '0', 10);
      if (Number.isFinite(ho)) this.hijriOffset = Math.max(-2, Math.min(2,ho));
    } catch { /* corrupt */ }

    const hl = localStorage.getItem(LS_HIGHLAT) as HighLatitudeRuleKey | null;
    if (hl === 'auto' || hl === 'middleOfNight' || hl === 'seventhOfNight' || hl === 'twilightAngle') {
      this.highLatRule = hl;
    }

    const mn = localStorage.getItem(LS_MIDNIGHT) as MidnightModeKey | null;
    if (mn === 'standard' || mn === 'jafari') {
      this.midnightMode = mn;
    }

    const sf = localStorage.getItem(LS_SHAFAQ) as ShafaqKey | null;
    if (sf === 'general' || sf === 'ahmer' || sf === 'abyad') {
      this.shafaq = sf;
    }

    try {
      const customStr = localStorage.getItem(LS_CUSTOM);
      if (customStr) {
        const parsed = JSON.parse(customStr) as Partial<CustomMethodConfig>;
        Object.assign(this.customMethod, parsed);
      }
    } catch { /* corrupt */ }
  }

  private async requestGeolocation(): Promise<void> {
    if (!navigator.geolocation) return;

    try {
      const position = await new Promise<GeolocationPosition>((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject, {
          enableHighAccuracy: false,
          timeout: 10_000,
          maximumAge: 600_000,
        });
      });

      this.location = {
        lat: position.coords.latitude,
        lng: position.coords.longitude,
      };
      localStorage.setItem(LS_LOCATION, JSON.stringify(this.location));
    } catch {
      // Geolocation denied or unavailable
    }
  }

  private computeLocalTimes(): void {
    if (!this.location) return;
    const today = new Date().toDateString();
    if (this.cachedDate === today && this.cachedAdhanTimes) return;

    const coords = new Coordinates(this.location.lat, this.location.lng);
    const params = getCalculationParams(this.method, this.madhab, coords, {
      tune: this.tune,
      highLatRule: this.highLatRule,
      custom: this.customMethod,
    });
    this.cachedAdhanTimes = new AdhanPrayerTimes(coords, new Date(), params);
    this.cachedDate = today;
  }

  private getAdhanTimes(): AdhanPrayerTimes | null {
    this.computeLocalTimes();
    return this.cachedAdhanTimes;
  }

  private computeNextPrayer(times: AdhanPrayerTimes): NextPrayer | null {
    const now = new Date();

    const entries: Array<{ key: PrayerKey; time: Date }> = [
      { key: 'fajr', time: times.fajr },
      { key: 'sunrise', time: times.sunrise },
      { key: 'dhuhr', time: times.dhuhr },
      { key: 'asr', time: times.asr },
      { key: 'maghrib', time: times.maghrib },
      { key: 'isha', time: times.isha },
    ];

    for (const entry of entries) {
      if (entry.time > now) {
        const minutesUntil = Math.round((entry.time.getTime() - now.getTime()) / 60_000);
        return {
          name: entry.key,
          time: entry.time,
          minutesUntil,
        };
      }
    }

    return null;
  }

  private showPrayerNotification(prayer: PrayerKey, time: Date): void {
    if (Notification.permission !== 'granted') return;

    const name = this.getPrayerName(prayer);
    const offset = this.reminderOffsets[prayer];
    const timeStr = this.formatTime(time);

    try {
      new Notification(`${name} in ${offset} minutes`, {
        body: `${name} at ${timeStr}`,
        icon: '/icons/icon-192x192.png',
        badge: '/icons/icon-72x72.png',
        tag: `prayer-reminder-${prayer}`,
        silent: false,
      });
    } catch {
      // Notification API not available in this context
    }
  }

  private showAthanNotification(prayer: PrayerKey): void {
    if (Notification.permission !== 'granted') return;

    const name = this.getPrayerName(prayer);

    try {
      new Notification(`${name}`, {
        body: `It is time for ${name} prayer`,
        icon: '/icons/icon-192x192.png',
        badge: '/icons/icon-72x72.png',
        tag: `prayer-athan-${prayer}`,
        silent: false,
      });
    } catch {
      // Notification API not available in this context
    }
  }

  private clearScheduledNotifications(): void {
    for (const id of this.scheduledNotifications) {
      window.clearTimeout(id);
    }
    this.scheduledNotifications = [];
  }
}

// ---------------------------------------------------------------------------
// Singleton
// ---------------------------------------------------------------------------

export const prayerTimeService = new PrayerTimeService();
